/**
 * Utility functions for date formatting and manipulation
 */

/**
 * Format a date to a human-readable string
 * @param {Date|string|number} date - Date to format
 * @param {Object} options - Formatting options
 * @param {string} options.format - Format type ('full', 'date', 'time', 'dateTime', 'relative')
 * @param {string} options.locale - Locale to use for formatting (default: 'en-US')
 * @returns {string} Formatted date string
 */
export const formatDate = (date, options = {}) => {
  const { 
    format = 'full', 
    locale = 'en-US' 
  } = options;
  
  if (!date) return '';
  
  const dateObj = typeof date === 'string' || typeof date === 'number' 
    ? new Date(date) 
    : date;
  
  if (!(dateObj instanceof Date) || isNaN(dateObj)) {
    console.error('Invalid date provided to formatDate:', date);
    return '';
  }
  
  switch (format) {
    case 'full':
      return dateObj.toLocaleDateString(locale, { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    
    case 'date':
      return dateObj.toLocaleDateString(locale, { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    
    case 'shortDate':
      return dateObj.toLocaleDateString(locale, { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    
    case 'time':
      return dateObj.toLocaleTimeString(locale, { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    
    case 'dateTime':
      return dateObj.toLocaleDateString(locale, { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    
    case 'relative':
      return formatRelativeTime(dateObj);
    
    case 'iso':
      return dateObj.toISOString();
    
    default:
      return dateObj.toLocaleDateString(locale);
  }
};

/**
 * Format a date as a relative time string (e.g., "2 hours ago")
 * @param {Date} date - Date to format
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date) => {
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  
  if (diffInSeconds < 60) {
    return diffInSeconds <= 5 ? 'just now' : `${diffInSeconds} seconds ago`;
  }
  
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return diffInMinutes === 1 ? '1 minute ago' : `${diffInMinutes} minutes ago`;
  }
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return diffInHours === 1 ? '1 hour ago' : `${diffInHours} hours ago`;
  }
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) {
    return diffInDays === 1 ? 'yesterday' : `${diffInDays} days ago`;
  }
  
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return diffInMonths === 1 ? '1 month ago' : `${diffInMonths} months ago`;
  }
  
  const diffInYears = Math.floor(diffInMonths / 12);
  return diffInYears === 1 ? '1 year ago' : `${diffInYears} years ago`;
};

/**
 * Get the start of a time period
 * @param {string} period - Period type ('day', 'week', 'month', 'year')
 * @param {Date} date - Date to get the start of the period for (default: current date)
 * @returns {Date} Start of the period
 */
export const getStartOfPeriod = (period, date = new Date()) => {
  const result = new Date(date);
  
  switch (period) {
    case 'day':
      result.setHours(0, 0, 0, 0);
      break;
    
    case 'week':
      const dayOfWeek = result.getDay();
      const diff = result.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust for Sunday
      result.setDate(diff);
      result.setHours(0, 0, 0, 0);
      break;
    
    case 'month':
      result.setDate(1);
      result.setHours(0, 0, 0, 0);
      break;
    
    case 'year':
      result.setMonth(0, 1);
      result.setHours(0, 0, 0, 0);
      break;
    
    default:
      console.error('Invalid period provided to getStartOfPeriod:', period);
  }
  
  return result;
};

/**
 * Get the end of a time period
 * @param {string} period - Period type ('day', 'week', 'month', 'year')
 * @param {Date} date - Date to get the end of the period for (default: current date)
 * @returns {Date} End of the period
 */
export const getEndOfPeriod = (period, date = new Date()) => {
  const result = new Date(date);
  
  switch (period) {
    case 'day':
      result.setHours(23, 59, 59, 999);
      break;
    
    case 'week':
      const dayOfWeek = result.getDay();
      const diff = result.getDate() + (dayOfWeek === 0 ? 0 : 7 - dayOfWeek);
      result.setDate(diff);
      result.setHours(23, 59, 59, 999);
      break;
    
    case 'month':
      result.setMonth(result.getMonth() + 1, 0);
      result.setHours(23, 59, 59, 999);
      break;
    
    case 'year':
      result.setFullYear(result.getFullYear() + 1, 0, 0);
      result.setHours(0, 0, 0, -1);
      break;
    
    default:
      console.error('Invalid period provided to getEndOfPeriod:', period);
  }
  
  return result;
};

/**
 * Add time to a date
 * @param {Date} date - Date to add time to
 * @param {number} amount - Amount to add
 * @param {string} unit - Unit of time ('seconds', 'minutes', 'hours', 'days', 'weeks', 'months', 'years')
 * @returns {Date} New date with added time
 */
export const addTime = (date, amount, unit) => {
  const result = new Date(date);
  
  switch (unit) {
    case 'seconds':
      result.setSeconds(result.getSeconds() + amount);
      break;
    
    case 'minutes':
      result.setMinutes(result.getMinutes() + amount);
      break;
    
    case 'hours':
      result.setHours(result.getHours() + amount);
      break;
    
    case 'days':
      result.setDate(result.getDate() + amount);
      break;
    
    case 'weeks':
      result.setDate(result.getDate() + (amount * 7));
      break;
    
    case 'months':
      result.setMonth(result.getMonth() + amount);
      break;
    
    case 'years':
      result.setFullYear(result.getFullYear() + amount);
      break;
    
    default:
      console.error('Invalid unit provided to addTime:', unit);
  }
  
  return result;
};

/**
 * Check if a date is today
 * @param {Date} date - Date to check
 * @returns {boolean} True if the date is today
 */
export const isToday = (date) => {
  const today = new Date();
  return date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();
};

/**
 * Check if a date is in the past
 * @param {Date} date - Date to check
 * @returns {boolean} True if the date is in the past
 */
export const isPast = (date) => {
  return date < new Date();
};

/**
 * Check if a date is in the future
 * @param {Date} date - Date to check
 * @returns {boolean} True if the date is in the future
 */
export const isFuture = (date) => {
  return date > new Date();
};

/**
 * Convert a UTC date string from server to local time
 * @param {string|Date} utcDate - UTC date string or Date object from server
 * @returns {Date} Date object in local timezone
 */
export const utcToLocal = (utcDate) => {
  if (!utcDate) return null;
  
  let date;
  if (typeof utcDate === 'string') {
    if (utcDate.endsWith('Z')) {
      date = new Date(utcDate);
    } else {
      date = new Date(utcDate + 'Z');
    }
  } else if (utcDate instanceof Date) {
    date = new Date(utcDate);
  } else {
    console.error('Invalid date format provided to utcToLocal:', utcDate);
    return null;
  }
  
  if (!date || isNaN(date)) {
    console.error('Invalid date after conversion in utcToLocal:', utcDate);
    return null;
  }
  
  return date;
};

/**
 * Format a UTC date string from server to local timezone with specified format
 * @param {string|Date} utcDate - UTC date string or Date object from server
 * @param {Object} options - Formatting options (same as formatDate)
 * @returns {string} Formatted date string in local timezone
 */
export const formatUtcToLocal = (utcDate, options = {}) => {
  const localDate = utcToLocal(utcDate);
  if (!localDate) return '';
  
  return formatDate(localDate, options);
};

/**
 * Get the user's timezone name
 * @returns {string} The user's timezone name (e.g., 'America/New_York')
 */
const getUserTimezoneName = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'Unknown';
  }
};

/**
 * Get the current timezone offset in hours (e.g., +5:30, -4:00)
 * @param {boolean} includeMinutes - Whether to include minutes in the offset
 * @returns {string} Formatted timezone offset
 */
export const getTimezoneOffset = (includeMinutes = true) => {
  const date = new Date();
  const offsetMinutes = date.getTimezoneOffset();
  const offsetHours = Math.abs(Math.floor(offsetMinutes / 60));
  const offsetMins = Math.abs(offsetMinutes % 60);
  const sign = offsetMinutes < 0 ? '+' : '-'; // Note: getTimezoneOffset returns inverse of expected sign
  
  if (includeMinutes) {
    return `${sign}${String(offsetHours).padStart(2, '0')}:${String(offsetMins).padStart(2, '0')}`;
  } else {
    return `${sign}${offsetHours}`;
  }
};

/**
 * Get the difference between two dates in the specified unit
 * @param {Date} date1 - First date
 * @param {Date} date2 - Second date
 * @param {string} unit - Unit of time ('seconds', 'minutes', 'hours', 'days', 'weeks', 'months', 'years')
 * @returns {number} Difference between the dates in the specified unit
 */
export const getDiff = (date1, date2, unit) => {
  const diffMs = date2 - date1;
  
  switch (unit) {
    case 'seconds':
      return Math.floor(diffMs / 1000);
    
    case 'minutes':
      return Math.floor(diffMs / (1000 * 60));
    
    case 'hours':
      return Math.floor(diffMs / (1000 * 60 * 60));
    
    case 'days':
      return Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    case 'weeks':
      return Math.floor(diffMs / (1000 * 60 * 60 * 24 * 7));
    
    case 'months': {
      const d1 = new Date(date1);
      const d2 = new Date(date2);
      return (d2.getFullYear() - d1.getFullYear()) * 12 + d2.getMonth() - d1.getMonth();
    }
    
    case 'years': {
      const d1 = new Date(date1);
      const d2 = new Date(date2);
      return d2.getFullYear() - d1.getFullYear();
    }
    
    default:
      console.error('Invalid unit provided to getDiff:', unit);
      return diffMs;
  }
};

/**
 * Format server UTC date to various common formats in user's local timezone
 * @param {string|Date} serverDate - UTC date from server
 * @param {string} format - Predefined format name: 
 *   'default' - "Jan 15, 2023, 3:30 PM"
 *   'short' - "01/15/2023 15:30"
 *   'long' - "January 15, 2023 at 3:30:45 PM"
 *   'dateOnly' - "Jan 15, 2023"
 *   'timeOnly' - "3:30 PM"
 *   'full' - "9/11/2025, 2:35:15 PM" (with seconds)
 *   'iso' - ISO format in local timezone
 *   'withTz' - With timezone name
 *   'debug' - Shows both UTC and local time for debugging
 * @returns {string} Formatted date in local timezone
 */
export const formatServerDate = (serverDate, format = 'default') => {
  if (!serverDate) return '';

  let localDate;

  try {
    if (serverDate instanceof Date) {
      localDate = new Date(serverDate);
    } else if (typeof serverDate === 'string') {
      // Normalize string:
      let cleaned = serverDate.trim();

      // Replace space with T (handles "2026-03-30 08:50:01")
      cleaned = cleaned.replace(' ', 'T');

      // Trim microseconds → milliseconds (JS supports only 3 digits)
      cleaned = cleaned.replace(/\.(\d{3})\d+/, '.$1');

      // Detect timezone (Z or ±HH:mm or ±HHmm)
      const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(cleaned);

      if (!hasTimezone) {
        cleaned += 'Z'; // assume UTC if missing
      }

      localDate = new Date(cleaned);
    } else {
      return '';
    }

    if (isNaN(localDate)) return '';
  } catch (e) {
    console.error('Date parsing error:', e);
    return '';
  }

  const locale = navigator.language || 'en-US';

  switch (format) {
    case 'default':
      return localDate.toLocaleString(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });

    case 'short':
      return localDate.toLocaleString(locale, {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });

    case 'long':
      return localDate
        .toLocaleString(locale, {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit'
        })
        .replace(',', ' at');

    case 'dateOnly':
      return localDate.toLocaleDateString(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });

    case 'timeOnly':
      return localDate.toLocaleTimeString(locale, {
        hour: 'numeric',
        minute: '2-digit'
      });

    case 'full':
      return localDate.toLocaleString(locale, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit'
      });

    case 'iso': {
      const pad = (n) => String(n).padStart(2, '0');
      return `${localDate.getFullYear()}-${pad(localDate.getMonth() + 1)}-${pad(localDate.getDate())}T${pad(localDate.getHours())}:${pad(localDate.getMinutes())}:${pad(localDate.getSeconds())}.${String(localDate.getMilliseconds()).padStart(3, '0')}`;
    }

    case 'withTz':
      return `${localDate.toLocaleString(locale)} (${getUserTimezoneName()})`;

    case 'debug':
      return `Local: ${localDate.toLocaleString(locale)} | UTC: ${localDate.toUTCString()} | TZ: ${getUserTimezoneName()}`;

    default:
      return localDate.toLocaleString(locale);
  }
};

